/**
 * Cross-tab coordination for the per-tab business feature: track which live tab
 * shows which business in a `localStorage` map `{ tabId: { businessId, ts } }`,
 * refreshed by a heartbeat and pruned once a tab's entry goes stale (dead tab).
 * Used only to recognise a Chrome-duplicated tab — its inherited id is already
 * live — so it can mint a fresh id. `BroadcastChannel` carries the live
 * duplicate-detection ping/pong, where waiting for the heartbeat would be too slow.
 *
 * Which business a fresh tab / login opens is NOT decided here — it follows the
 * shared identity token (see `adoptTabTokenAsIdentity` in utils/tabAuth). The
 * pure helpers below hold the decision logic and are unit-tested; the stateful
 * wrapper persists and broadcasts.
 */
import { getTabId } from 'utils/tabAuth';

const CHANNEL_NAME = 'heltar-tabs';
const OPEN_TABS_KEY = '_heltar_open_tabs';
export const HEARTBEAT_MS = 5000;
export const STALE_MS = 15000;

export type TabRecord = { businessId: number; ts: number };
export type OpenTabsMap = Record<string, TabRecord>;

export type RegistryMessage =
  | { type: 'claim'; tabId: string; businessId: number }
  | { type: 'release'; tabId: string }
  // Duplicate detection: a fresh tab asks whether its id is already live.
  | { type: 'ping'; tabId: string }
  | { type: 'pong'; tabId: string };

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

/** Drop entries whose heartbeat is older than the stale window (dead tabs). */
export const pruneStale = (
  map: OpenTabsMap,
  now: number,
  staleMs: number = STALE_MS,
): OpenTabsMap => {
  const out: OpenTabsMap = {};
  for (const [tabId, rec] of Object.entries(map)) {
    if (now - rec.ts < staleMs) out[tabId] = rec;
  }
  return out;
};

/**
 * The business a fresh tab — or a fresh login — should open: the preferred
 * business (the identity / last-active business) when the tab can still access
 * it, else the lowest accessible id.
 *
 * Deliberately ignores which businesses other tabs show: a new or duplicated tab
 * reopens the business the identity points at rather than spreading to a
 * different one; the user switches from there if they want. Returns `null` only
 * when the tab can access no business at all.
 */
export const pickPreferredBusiness = (
  accessibleIds: number[],
  preferredId?: number | null,
): number | null => {
  if (preferredId != null && accessibleIds.includes(preferredId)) {
    return preferredId;
  }
  return [...accessibleIds].sort((a, b) => a - b)[0] ?? null;
};

// ---------------------------------------------------------------------------
// Stateful layer (localStorage + BroadcastChannel)
// ---------------------------------------------------------------------------

const channel: BroadcastChannel | null =
  typeof BroadcastChannel !== 'undefined'
    ? new BroadcastChannel(CHANNEL_NAME)
    : null;

export const readOpenTabs = (): OpenTabsMap => {
  try {
    const raw = localStorage.getItem(OPEN_TABS_KEY);
    const map = raw ? (JSON.parse(raw) as OpenTabsMap) : {};
    return pruneStale(map, Date.now());
  } catch {
    return {};
  }
};

const writeOpenTabs = (map: OpenTabsMap): void => {
  localStorage.setItem(OPEN_TABS_KEY, JSON.stringify(map));
};

export const postMessage = (message: RegistryMessage): void => {
  channel?.postMessage(message);
};

/** Record (or refresh) this tab's hold on `businessId`. */
export const claimBusiness = (businessId: number): void => {
  const map = readOpenTabs();
  map[getTabId()] = { businessId, ts: Date.now() };
  writeOpenTabs(map);
  postMessage({ type: 'claim', tabId: getTabId(), businessId });
};

/** Remove this tab from the registry (on close / business change). */
export const releaseTab = (): void => {
  const map = readOpenTabs();
  delete map[getTabId()];
  writeOpenTabs(map);
  postMessage({ type: 'release', tabId: getTabId() });
};

export const subscribe = (
  handler: (message: RegistryMessage) => void,
): (() => void) => {
  if (!channel) return () => undefined;
  const listener = (event: MessageEvent<RegistryMessage>) =>
    handler(event.data);
  channel.addEventListener('message', listener);
  return () => channel.removeEventListener('message', listener);
};
